import BaseSelect from 'common/components/select/BaseSelect'
import {ArrowLeft, ArrowRight, Info} from 'lucide-react'
import React, {FC, ReactElement, ReactNode, useId} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'

import type {AllocationCounts} from '../../utils/teacherAllocationModel'

/** Filled share of the required assignments, 0–100. */
export const percentFilled = (counts: AllocationCounts) =>
  counts.total > 0 ? Math.round((counts.filled / counts.total) * 100) : 0

export const AllocationProgressBar: FC<{counts: AllocationCounts; label: string}> = ({
  counts,
  label,
}) => {
  const percent = percentFilled(counts)
  return (
    <div
      className='ta-progress'
      role='progressbar'
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={counts.total}
      aria-valuenow={counts.filled}
    >
      <div className='ta-progress__fill' style={{width: `${percent}%`}} />
    </div>
  )
}

/** Hover/focus tooltip in the allocation style (closed on arrival). */
export const AllocationTooltip: FC<{
  text: ReactNode
  placement?: 'top' | 'bottom' | 'left' | 'right'
  children: ReactElement
}> = ({text, placement = 'top', children}) => {
  const id = useId()
  return (
    <OverlayTrigger
      placement={placement}
      trigger={['hover', 'focus']}
      overlay={
        <Tooltip id={id} className='ta-tooltip'>
          {text}
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  )
}

/** The (i) icon with its own tooltip. */
export const InfoIconTooltip: FC<{text: string; label: string}> = ({text, label}) => (
  <AllocationTooltip text={text} placement='bottom'>
    <button type='button' className='ta-info-icon' aria-label={label}>
      <Info size={20} aria-hidden />
    </button>
  </AllocationTooltip>
)

/**
 * A labelled filter dropdown ("Branch: All") on the shared BaseSelect, styled to the allocation
 * UI. One option: shown selected, no All; several: All first (handover, Point 3 "Filters"). The
 * control reads "Label: value"; the menu lists the plain option names.
 */
export const AllocationFilterSelect: FC<{
  label: string
  value: string
  values: string[]
  hasAll: boolean
  allLabel: string
  optionLabel?: (value: string) => string
  disabled?: boolean
  onChange: (value: string) => void
}> = ({label, value, values, hasAll, allLabel, optionLabel, disabled, onChange}) => {
  const options = [
    ...(hasAll ? [{value: '', label: allLabel}] : []),
    ...values.map((v) => ({value: v, label: optionLabel ? optionLabel(v) : v})),
  ]
  return (
    <div className='ta-filter-select'>
      <BaseSelect
        options={options}
        currentValue={value}
        onSelectionChange={({values: picked}) => onChange(String(picked ?? ''))}
        isSearchable={false}
        isClearable={false}
        isDisabled={disabled || values.length === 0}
        aria-label={label}
        selectClassName='ta-filter-select__control'
        formatOptionLabel={(option: any, {context}: {context: 'menu' | 'value'}) =>
          context === 'value' ? (
            <span>
              <span className='ta-filter-select__label'>{label}:</span> {option.label}
            </span>
          ) : (
            option.label
          )
        }
      />
    </div>
  )
}

/**
 * Back · hint · Continue. Continue stays disabled while required assignments remain (the hint
 * says why) and leads to the existing next stage when none do.
 */
export const AllocationFooter: FC<{
  backLabel: string
  backVariant: 'solid' | 'outline'
  onBack: () => void
  backDisabled?: boolean
  canContinue: boolean
  hint: string
  busy?: boolean
  onContinue: () => void
}> = ({backLabel, backVariant, onBack, backDisabled, canContinue, hint, busy, onContinue}) => {
  const intl = useIntl()
  return (
    <div className='ta-footer'>
      <button
        type='button'
        className={backVariant === 'solid' ? 'ta-btn ta-btn--primary' : 'ta-btn ta-btn--outline'}
        onClick={onBack}
        disabled={backDisabled}
      >
        <ArrowLeft size={18} aria-hidden />
        {backLabel}
      </button>
      <div className='ta-footer__cta'>
        {!canContinue && <span className='ta-footer__hint'>{hint}</span>}
        <button
          type='button'
          className='ta-btn ta-btn--primary ta-btn--continue'
          disabled={!canContinue || busy}
          onClick={onContinue}
        >
          {busy && <span className='spinner-border spinner-border-sm' aria-hidden />}
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.CONTINUE'})}
          {!busy && <ArrowRight size={18} aria-hidden />}
        </button>
      </div>
    </div>
  )
}
